import { dispatch, store } from '@/store';
import {
  type TAuthMode,
  closeAuthOverlay,
  openAuthOverlay,
  setAuthMode as setAuthModeAction
} from '@/store/auth.slice';

/**
 * Императивный контроллер оверлея авторизации. Само состояние (open/mode) живёт в Redux
 * (store/auth.slice), а здесь — промис openAuth для ожидающих (мост sso/auth-required),
 * который в стор не положить. Функции работают и вне React (через bound dispatch).
 */
let resolvers: Array<(value: boolean) => void> = [];

/**
 * Открыть оверлей. Резолвится при закрытии: `true` — пользователь авторизовался,
 * `false` — закрыл без авторизации. Single-flight: повторные вызовы ждут тот же результат.
 */
export function openAuth(mode: TAuthMode = 'login'): Promise<boolean> {
  dispatch(openAuthOverlay(mode));
  return new Promise<boolean>((resolve) => {
    resolvers.push(resolve);
  });
}

/**
 * Закрыть оверлей и зарезолвить всех ожидающих openAuth. Резолвится, когда оверлей
 * снял свою history-запись: навигацию после закрытия (например, на восстановление
 * пароля) делай только после этого, иначе запоздавший POP её откатит.
 */
export function closeAuth(success = false): Promise<void> {
  dispatch(closeAuthOverlay());
  const pending = resolvers;
  resolvers = [];
  let done: () => void = () => {};
  const closed = new Promise<void>((resolve) => {
    done = resolve;
  });
  // Ожидающих openAuth резолвим раньше, чем `closed`: их навигация (ProtectedRoute →
  // главная с replace) отрабатывает первой, навигация вызывающего — поверх неё.
  const settle = () => {
    pending.forEach((resolve) => resolve(success));
    done();
  };

  // Оверлей добавлял свою history-запись (см. AuthOverlay) — снимаем её здесь.
  // history.back() асинхронный, поэтому ожидающих резолвим только по факту POP: иначе их
  // навигация (ProtectedRoute → navigate(..., { replace: true })) перезапишет запись оверлея,
  // а прилетевший следом POP откатит всё назад — на тот самый закрытый маршрут.
  // Закрытие через браузерный «назад»: запись уже снята, флага нет → резолвим сразу.
  if (window.history.state?.__authOverlay) {
    // Метим служебный POP, чтобы back-блокер webview-сервиса пропустил его (не принял за
    // внутренний «назад» сервиса и не отправил фантомный back.pressed).
    armOverlayBackBypass();
    window.addEventListener('popstate', settle, { once: true });
    window.history.back();
    return closed;
  }

  settle();
  return closed;
}

/** Переключить режим без навигации (login ↔ register). */
export function setAuthMode(mode: TAuthMode): void {
  dispatch(setAuthModeAction(mode));
}

/** Открыт ли сейчас оверлей — верхний слой над контентом (в т.ч. над webview-сервисом). */
export function isAuthOverlayOpen(): boolean {
  return store.getState().auth.overlay.open;
}

/**
 * Координация «назад» с back-блокером webview-сервиса. Оверлей рисует поверх сервиса, поэтому
 * системный/браузерный «назад» должен принадлежать оверлею, а не сервису. Пока оверлей открыт —
 * блокер сервиса не трогает POP (см. isAuthOverlayOpen). А когда оверлей закрывается программно
 * (×/бэкдроп/Esc/успех), он снимает свою history-запись через history.back(): этот служебный POP
 * помечаем флагом, чтобы блокер сервиса пропустил его, а не принял за внутренний «назад» сервиса.
 */
let backBypassArmed = false;

/** Пометить следующий POP как служебный (снятие history-записи оверлея). */
export function armOverlayBackBypass(): void {
  backBypassArmed = true;
}

/** Проверить и снять флаг служебного POP оверлея (одноразово). */
export function consumeOverlayBackBypass(): boolean {
  if (!backBypassArmed) return false;
  backBypassArmed = false;
  return true;
}
